(() => {
  if (window.SkyProgressiveLoading) return;

  const supportsObserver = 'IntersectionObserver' in window;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = Boolean(navigator.connection && navigator.connection.saveData);
  const loadElements = new Set();
  const visibilityElements = new Set();
  const revealElements = new Set();

  const dispatch = (element, name, detail = {}) => {
    element.dispatchEvent(new CustomEvent(name, { bubbles: false, detail }));
  };

  const activate = (element, trigger) => {
    if (!element || element.dataset.skyLoadState === 'active') return;
    element.dataset.skyLoadState = 'active';
    dispatch(element, 'sky:load', {
      trigger,
      allowHeavy: !saveData
    });
  };

  const loadObserver = supportsObserver
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            loadObserver.unobserve(entry.target);
            activate(entry.target, 'viewport');
          });
        },
        { rootMargin: '240px 0px' }
      )
    : null;

  const visibilityObserver = supportsObserver
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const visible = entry.isIntersecting && entry.intersectionRatio > 0;
            entry.target.dataset.skyVisibilityState = visible ? 'visible' : 'hidden';
            dispatch(entry.target, 'sky:visibility', { visible });
          });
        },
        { threshold: 0 }
      )
    : null;

  const revealObserver = supportsObserver && !reduceMotion
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            revealObserver.unobserve(entry.target);
            entry.target.dataset.skyRevealState = 'visible';
          });
        },
        { rootMargin: '0px 0px -8%', threshold: 0.08 }
      )
    : null;

  const bindInteraction = (element) => {
    if (element.dataset.skyLoadInteractionBound === 'true') return;
    element.dataset.skyLoadInteractionBound = 'true';

    const onInteraction = (event) => activate(element, event.type);
    element.addEventListener('pointerenter', onInteraction, { once: true });
    element.addEventListener('focusin', onInteraction, { once: true });
    element.addEventListener('touchstart', onInteraction, { once: true, passive: true });
  };

  const register = (root = document) => {
    const selector = '[data-sky-load], [data-sky-visibility], [data-sky-reveal]';
    const scope = root instanceof Element
      ? [root, ...root.querySelectorAll(selector)]
      : Array.from(root.querySelectorAll(selector));

    scope.forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      const loadMode = element.dataset.skyLoad;
      if (loadMode && !loadElements.has(element)) {
        loadElements.add(element);
        element.dataset.skyLoadState = 'pending';

        if (loadMode === 'viewport' || loadMode === 'hybrid') {
          if (loadObserver) loadObserver.observe(element);
          else activate(element, 'fallback');
        }
        if (loadMode === 'interaction' || loadMode === 'hybrid') bindInteraction(element);
      }

      if (element.hasAttribute('data-sky-visibility') && !visibilityElements.has(element)) {
        visibilityElements.add(element);
        if (visibilityObserver) visibilityObserver.observe(element);
        else dispatch(element, 'sky:visibility', { visible: true });
      }

      if (element.hasAttribute('data-sky-reveal') && !revealElements.has(element)) {
        revealElements.add(element);
        if (revealObserver) {
          element.dataset.skyRevealState = 'pending';
          revealObserver.observe(element);
        } else {
          element.dataset.skyRevealState = 'visible';
        }
      }
    });
  };

  const unregister = (root) => {
    if (!(root instanceof Element)) return;
    const scope = [
      root,
      ...root.querySelectorAll('[data-sky-load], [data-sky-visibility], [data-sky-reveal]')
    ];

    scope.forEach((element) => {
      if (loadElements.delete(element) && loadObserver) loadObserver.unobserve(element);
      if (visibilityElements.delete(element) && visibilityObserver) visibilityObserver.unobserve(element);
      if (revealElements.delete(element) && revealObserver) revealObserver.unobserve(element);
    });
  };

  window.SkyProgressiveLoading = { register, unregister, activate };
  register(document);

  document.addEventListener('shopify:section:load', (event) => register(event.target));
  document.addEventListener('shopify:section:unload', (event) => unregister(event.target));
})();
